import { useMemo, useState } from 'react';
import { api, queryString } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useAuth } from '../lib/auth.tsx';
import { EventDetailModal } from '../components/EventDetailModal.tsx';
import { EmptyState, ErrorState, Field, Loading, Modal, PageHeader } from '../components/ui.tsx';
import { EVENT_TYPE_LABEL, daysUntil, formatDateLong, todayIso } from '../lib/format.ts';
import { integrationNotConfiguredNotice } from '../lib/integrations.ts';
import type { CalendarEvent, CalendarEventType } from '../../../shared/types.ts';

const TYPES: CalendarEventType[] = ['efemeride', 'turne', 'acao_interna', 'outro'];

export function CalendarPage() {
  const { can } = useAuth();
  const [type, setType] = useState('');
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<CalendarEvent | null>(null);

  const from = todayIso();
  const query = queryString({ from, type: type || undefined });
  const events = useAsync(() => api.get<CalendarEvent[]>(`/calendar/events${query}`), [query]);

  async function syncTour() {
    setSyncing(true);
    setNotice(null);
    try {
      const result = await api.post<{
        configured: boolean;
        inserted: number;
        updated: number;
        failed: string[];
      }>('/calendar/sync-tour');
      if (!result.configured) {
        setNotice(
          integrationNotConfiguredNotice(
            'Sincronização de turnê',
            'BANDSINTOWN_APP_ID',
            can('superadmin'),
          ),
        );
      } else {
        setNotice(
          result.inserted === 0 && result.updated === 0
            ? 'Nenhuma novidade na turnê.'
            : `${result.inserted} data(s) nova(s), ${result.updated} atualizada(s).`,
        );
        if (result.failed.length > 0) {
          setNotice((n) => `${n ?? ''} Falhas: ${result.failed.join('; ')}`);
        }
      }
      events.reload();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Falha ao sincronizar a turnê.');
    } finally {
      setSyncing(false);
    }
  }

  const grouped = useMemo(() => {
    const byMonth = new Map<string, CalendarEvent[]>();
    for (const event of events.data ?? []) {
      const key = event.startDate.slice(0, 7);
      const list = byMonth.get(key) ?? [];
      list.push(event);
      byMonth.set(key, list);
    }
    return [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [events.data]);

  return (
    <>
      <PageHeader
        title="Calendário"
        subtitle={`Eventos a partir de ${formatDateLong(from)} — efemérides da banda, datas da turnê e ações internas.`}
        actions={
          can('gerente') && (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void syncTour()}
                disabled={syncing}
              >
                {syncing ? 'Sincronizando…' : 'Sincronizar turnê'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                Novo evento
              </button>
            </>
          )
        }
      />

      {notice && <p className="callout">{notice}</p>}

      <div className="toolbar card">
        <select
          className="select"
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label="Filtrar por tipo de evento"
        >
          <option value="">Todos os tipos</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {EVENT_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      {events.loading && <Loading label="Carregando calendário…" />}
      {events.error && <ErrorState message={events.error} onRetry={events.reload} />}

      {events.data && events.data.length === 0 && (
        <EmptyState title="Nenhum evento à frente." hint="Cadastre um evento ou rode o seed." />
      )}

      {grouped.map(([month, items]) => (
        <section className="card" key={month}>
          <div className="card__header">
            <h2 className="text-title">{monthLabel(month)}</h2>
            <span className="text-micro text-muted">{items.length} evento(s)</span>
          </div>
          <ul className="timeline">
            {items.map((event) => {
              const days = daysUntil(event.startDate);
              return (
                <li key={event.id} className={`timeline__item timeline__item--${event.eventType}`}>
                  <button
                    type="button"
                    className="timeline__link"
                    onClick={() => setOpenEvent(event)}
                  >
                    <div className="timeline__date">
                      <strong className="tabular-nums">{event.startDate.slice(8, 10)}</strong>
                      <span className="text-micro text-muted">
                        {days === 0 ? 'hoje' : days === 1 ? 'amanhã' : `em ${days}d`}
                      </span>
                    </div>
                    <div className="timeline__body">
                      <div className="timeline__head">
                        <span className="text-body-strong">{event.title}</span>
                        <span className="badge badge-status-neutral">
                          {EVENT_TYPE_LABEL[event.eventType]}
                        </span>
                        {event.linkedActionId && (
                          <span className="text-micro text-muted">· ação vinculada</span>
                        )}
                      </div>
                      {event.description && (
                        <p className="text-micro text-muted timeline__description">
                          {event.description}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {creating && (
        <EventForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            events.reload();
          }}
        />
      )}

      {openEvent && (
        <EventDetailModal
          event={openEvent}
          canEditAction={can('gerente')}
          onClose={() => setOpenEvent(null)}
        />
      )}
    </>
  );
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  const label = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function EventForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    eventType: 'acao_interna' as CalendarEventType,
    startDate: todayIso(),
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title.trim()) {
      setError('Informe o título.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/calendar/events', {
        title: form.title.trim(),
        description: form.description.trim() || null,
        eventType: form.eventType,
        startDate: form.startDate,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar.');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Novo evento"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Salvando…' : 'Criar evento'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Título">
          <input
            className="input"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
        </Field>
        <Field label="Descrição">
          <textarea
            className="textarea"
            rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </Field>
        <div className="row">
          <Field label="Tipo">
            <select
              className="select"
              value={form.eventType}
              onChange={(e) =>
                setForm((f) => ({ ...f, eventType: e.target.value as CalendarEventType }))
              }
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {EVENT_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Data">
            <input
              className="input"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
            />
          </Field>
        </div>
        {error && (
          <p className="state-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
